import React from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Stack,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";

interface CardPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  totalAmount: number;
  paymentMethod: "card" | "benefitpay";
}

export const CardPaymentDialog: React.FC<CardPaymentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
  paymentMethod,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          minWidth: 480,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#1E8078",
          py: 3,
          px: 3,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Icon sx={{ fontSize: 28 }}>
          {paymentMethod === "card" ? "credit_card" : "qr_code"}
        </Icon>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {paymentMethod === "card" ? "Card Payment" : "BenefitPay"}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={4}>
          {/* Amount Display */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#1E8078", 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha("#1E8078", 0.1)}`,
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: alpha(theme.palette.text.primary, 0.6),
                mb: 1,
                fontWeight: 500,
              }}
            >
              Amount Due
            </Typography>
            <Typography
              variant="h3"
              sx={{ color: "#1E8078", fontWeight: 700, mb: 2 }}
            >
              BHD {totalAmount.toFixed(3)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: alpha(theme.palette.text.primary, 0.6),
              }}
            >
              <Icon sx={{ fontSize: 20 }}>info</Icon>
              <Typography variant="body2">
                {paymentMethod === "card"
                  ? "Please process the payment using the card terminal"
                  : "Please scan the QR code using BenefitPay app"}
              </Typography>
            </Box>
          </Paper>

          {/* Payment Status */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                mb: 3,
                color: "#1E8078",
                fontWeight: 600,
              }}
            >
              Payment Status
            </Typography>
            <Stack spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => onConfirm(false)}
                sx={{
                  py: 2,
                  borderColor: alpha(theme.palette.error.main, 0.3),
                  color: theme.palette.error.main,
                  backgroundColor: alpha(theme.palette.error.main, 0.02),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.05),
                    borderColor: theme.palette.error.main,
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Icon>cancel</Icon>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Payment Failed
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(theme.palette.error.main, 0.8) }}
                  >
                    Click here if the payment was unsuccessful
                  </Typography>
                </Box>
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => onConfirm(true)}
                sx={{
                  py: 2,
                  borderColor: alpha("#1E8078", 0.3),
                  color: "#1E8078",
                  backgroundColor: alpha("#1E8078", 0.02),
                  "&:hover": {
                    backgroundColor: alpha("#1E8078", 0.05),
                    borderColor: "#1E8078",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Icon>check_circle</Icon>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Payment Successful
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: alpha("#1E8078", 0.8) }}
                  >
                    Click here if the payment was completed
                  </Typography>
                </Box>
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderTop: `1px solid ${alpha("#1E8078", 0.1)}`,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: alpha("#1E8078", 0.3),
            color: "#1E8078",
            "&:hover": {
              borderColor: "#1E8078",
              backgroundColor: alpha("#1E8078", 0.05),
            },
          }}
        >
          Cancel Payment
        </Button>
      </Box>
    </Dialog>
  );
};
